import {
    Button, Component, TableCellProps, TableRow, TableRowToolsCell, TableRowToolsViewer
} from "@mcleod/components";

const appToolsCellProps: Partial<TableCellProps> = {
    padding: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    margin: 0,
    marginTop: 1,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    borderBottomWidth: 1,
    borderBottomColor: "#AAA"
}
const singleToolWidth = 70;

export class AppTableRowToolsCellReveal {
    private static activeToolCell: TableRowToolsCell | null = null;

    private readonly cell: TableRowToolsCell;
    private readonly viewer: TableRowToolsViewer;
    private readonly tools: Component[];
    private readonly revealAmount: number = 150; // how much to reveal when swiped
    private readonly swipeThreshold: number = 50; // minimum distance to trigger reveal
    private swipeStartX: number = 0;

    public static addHandler(cell: TableRowToolsCell, viewer: TableRowToolsViewer, tools: Component[]) {
        new AppTableRowToolsCellReveal(cell, viewer, tools);
    }

    private constructor(cell: TableRowToolsCell, viewer: TableRowToolsViewer, tools: Component[]) {
        this.cell = cell;
        this.viewer = viewer;
        this.tools = tools;

        this.cell.setProps(appToolsCellProps);
        this.cell.fixedWidth = singleToolWidth;
        this.cell["revealHandler"] = this;
        this.revealAmount = this.calculateRevealAmount();
        this.setupTouchHandling();

        this.addClickListenerToTools(this.tools);
    }

    private get cellElement() {
        return this.cell._element;
    }

    private get rowElement() {
        return this.cell.row._element;
    }

    private static get activeToolRevealHandler(): AppTableRowToolsCellReveal | null {
        return this.activeToolCell?.["revealHandler"] || null;
    }

    // Calculate the reveal amount based on the number of tools (min 1 tool width)
    private calculateRevealAmount(): number {
        const toolCount = Math.max(1, this.tools.length);
        return Math.max(singleToolWidth, toolCount * singleToolWidth);
    }

    private manageCellWidth() {
        this.cell.fixedWidth = this.revealAmount;
        this.cell.toolsPanel.width = this.revealAmount;
        if (this.viewer) {
            // Since app will have fewer tools, there may be a tool that we ultimately don't show. Particularly
            // in 'Advanced Search' mode where we'd only see an edit button, but single-taps on rows
            // handle edits. Since that's the case, we wouldn't create a toolsViewer.
            this.viewer.width = this.revealAmount;
        }
        this.cell.table.toolsCellAdded(0);
    }

    private setupTouchHandling(): void {
        this.rowElement.addEventListener('touchstart', this.handleRowTouch.bind(this), { passive: true });
        this.rowElement.addEventListener('touchmove', this.handleRowTouch.bind(this), { passive: false });
        this.rowElement.addEventListener('touchend', this.handleRowTouch.bind(this), { passive: true });

        // Position tools absolutely to the right of the row
        this.cellElement.style.position = 'absolute';
        this.cellElement.style.right = `-${this.revealAmount}px`;
        this.cellElement.style.top = '0';
        this.cellElement.style.height = '100%';
        this.cellElement.style.width = `${this.revealAmount}px`;

        // If more than one column, then we aren't using a single-column table row,
        // which means we haven't set up this layout to be compatible with mobile.
        // If that's the case, show the table row as-is, warts and all. We will setup
        // tools cell. It won't be visible if the row is running off the screen.
        this.rowElement.style.display = 'grid';
        // this.rowElement.style.gridTemplateColumns = `repeat(${ this.cell.row?.table?.columns?.length || 1 }, 1fr)`;
        this.rowElement.style.transition = 'transform 0.3s ease';
    }

    private handleRowTouch(event: TouchEvent): void {
        const type = event.type;

        switch(type) {
            case 'touchstart':
                this.handleTouchStart(event);
                break;
            case 'touchmove':
                this.handleTouchMove(event);
                break;
            case 'touchend':
                this.handleTouchEnd(event);
                break;
        }
    }

    private handleTouchStart(event: TouchEvent): void {
        this.manageCellWidth();

        // Skip if touching a tool button when tools are revealed
        const target = event.target as HTMLElement;
        if (this.isRevealed && this.isToolElement(target)) {
            return;
        }

        if (event.touches.length === 1) {
            this.swipeStartX = event.touches[0].clientX;

            // Reset other revealed rows
            if (AppTableRowToolsCellReveal.activeToolCell &&
                AppTableRowToolsCellReveal.activeToolCell !== this.cell &&
                AppTableRowToolsCellReveal.activeToolRevealHandler.isRevealed) {
                AppTableRowToolsCellReveal.activeToolRevealHandler.isRevealed = false;
            }
        }
    }

    private handleTouchMove(event: TouchEvent): void {
        // Skip if we didn't register a valid touchstart or if touching a tool
        if (!this.swipeStartX || (this.isRevealed && this.isToolElement(event.target as HTMLElement))) {
            return;
        }

        const currentX = event.touches[0].clientX;
        const diff = this.swipeStartX - currentX;

        // Only allow left swipes (positive diff)
        if (diff > 0) {
            // Make sure other rows are closed
            if (AppTableRowToolsCellReveal.activeToolCell && AppTableRowToolsCellReveal.activeToolCell !== this.cell) {
                AppTableRowToolsCellReveal.activeToolRevealHandler.isRevealed = false;
            }

            // Ensure the tools cell is added to the row
            if (!this.rowElement.contains(this.cellElement)) {
                this.rowElement.appendChild(this.cellElement);
            }

            // Limit the swipe to the reveal amount
            const translateX = Math.min(diff, this.revealAmount);
            this.rowElement.style.transform = `translateX(-${translateX}px)`;

            // Prevent scrolling while swiping
            event.preventDefault();

            // Set this tool cell as having the active, visible menu
            this.isRevealed = true;
        }
    }

    private handleTouchEnd(event: TouchEvent): void {
        // Skip if we didn't register a valid touchstart
        if (!this.swipeStartX) {
            return;
        }

        // If touching a tool button when tools are revealed, handle the tool tap
        const target = event.target as HTMLElement;
        if (this.isRevealed && this.isToolElement(target)) {
            return;
        }
        const currentX = event.changedTouches[0].clientX;
        const diff = this.swipeStartX - currentX;
        this.isRevealed = diff > this.swipeThreshold;
        this.swipeStartX = null;
    }

    private get isRevealed(): boolean {
        return this === AppTableRowToolsCellReveal.activeToolRevealHandler;
    }

    private set isRevealed(value: boolean) {
        if (value) {
            if (AppTableRowToolsCellReveal.activeToolCell && AppTableRowToolsCellReveal.activeToolCell !== this.cell) {
                AppTableRowToolsCellReveal.activeToolRevealHandler.isRevealed = false;
            }
            this.revealTools();
            AppTableRowToolsCellReveal.activeToolCell = this.cell;
        } else {
            this.hideTools()
            AppTableRowToolsCellReveal.activeToolCell = null;
        }
    }

    // Helper method to check if an element is a tool button
    private isToolElement(element: HTMLElement): boolean {
        // Walk up the DOM tree to check if the element is part of a tool
        let current: HTMLElement | null = element;
        while (current && current !== this.rowElement) {
            if (current["_mcleodComponent"] &&
                (current["_mcleodComponent"] instanceof Button ||
                    current["_mcleodComponent"] === this.viewer)) {
                return true;
            }
            current = current.parentElement;
        }
        return false;
    }

    private revealTools(): void {
        if (!this.isRevealed) {
            this.rowElement.style.transform = `translateX(-${this.revealAmount}px)`;
            this.cell.fill();
            AppTableRowToolsCellReveal.addGlobalTouchListener();
        }
    }

    private hideTools(): void {
        if (this.isRevealed) {
            this.rowElement.style.transform = 'translateX(0)';
            // Remove the tools cell from the DOM after the transition
            this.rowElement.addEventListener('transitionend', this.handleHideToolsTransitionEnd);
        }
    }

    private handleHideToolsTransitionEnd = (event: TransitionEvent): void => {
        if (this.rowElement.getBoundingClientRect().x < 0) {
            return;
        }
        if (this.cellElement.parentNode === this.rowElement) {
            this.rowElement.removeChild(this.cellElement);
        }
        event?.stopPropagation();
        this.rowElement.removeEventListener('transitionend', this.handleHideToolsTransitionEnd);
    }

    private static addGlobalTouchListener(): void {
        document.addEventListener(
            'touchstart', this.handleGlobalTouch, { once: true, capture: true, passive: false }
        );
    }

    // Ensures that any touches outside the tools cell will close the menu.
    // User will have to perform their touch again to perform any intended action.
    private static handleGlobalTouch = (event: TouchEvent): void => {
        if (!AppTableRowToolsCellReveal.activeToolCell) {
            return;
        }

        let currentElement = event.target as HTMLElement;
        let mcleodComponent: any;

        while (currentElement && currentElement !== document.body) {
            mcleodComponent = currentElement["_mcleodComponent"];
            if (mcleodComponent) {
                if (mcleodComponent === AppTableRowToolsCellReveal.activeToolCell) {
                    // If the target is the tools cell an action has been selected
                    // Selecting an action will handle the closing of the tool menu.
                    return;
                } else if (mcleodComponent instanceof TableRow) {
                    // Prevent default only for table row actions. Closes menu. Action will have to be made again.
                    // Other actions proceed normally while also closing the menu.
                    // Could move `.preventDefault()` before `.isRevealed = false` to close menu and require
                    // all actions to be performed again if the menu is open, should we find this is necessary.
                    event.preventDefault();
                }
            }
            currentElement = currentElement.parentElement;
        }
        AppTableRowToolsCellReveal.activeToolRevealHandler.isRevealed = false;
    };

    private addClickListenerToTools(tools: Component[]): void {
        tools.forEach((tool: Component) => {
            tool.addClickListener(() => {
                setTimeout(() => this.hideTools(), 200);
            }, false, true);
        });
    }
}
